import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { CallPlaybackButton } from './CallPlaybackButton';
import { CallAppointmentProposals } from './CallAppointmentProposals';
import { FrontOfficeCallRelink } from './CallRelink';
import { IntakeRecordingPlayback } from './IntakeRecordingPlayback';
import { ShowFeature } from '@/components/access/ShowFeature';
import { cn } from '@/lib/utils';
import { associationLabel, cancelRequestNote } from '@/lib/ocis/customerReply';
import { 
  MessageSquare, 
  Phone, 
  Wrench, 
  Clock, 
  Play, 
  Pause, 
  CheckCircle2, 
  Calendar,
  ParkingCircle,
  Package,
  Bell,
  AlertTriangle,
  Bot,
  Mic,
  Pin,
  User,
  Send,
  MessageCircle,
  Plus,
  Minus,
  FileText,
  Shield,
  ShieldCheck,
  Compass
} from 'lucide-react';
import type { ThreadEvent } from '@/hooks/useTicketThread';

interface ThreadEventItemProps {
  event: ThreadEvent;
  isLast?: boolean;
}

// Event type to icon/color mapping
const EVENT_CONFIG: Record<string, { 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
  label: string;
}> = {
  // Ticket lifecycle
  ticket_created: { icon: Wrench, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Created' },
  ticket_assigned: { icon: User, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Assigned' },
  ticket_started: { icon: Play, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Started' },
  ticket_paused: { icon: Pause, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Paused' },
  ticket_resumed: { icon: Play, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Resumed' },
  ticket_completed: { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Completed' },
  ticket_date_changed: { icon: Calendar, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Date Changed' },
  ticket_moved_to_parking_lot: { icon: ParkingCircle, color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Parked' },
  ticket_moved_from_parking_lot: { icon: ParkingCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Unparked' },
  
  // Communication
  sms_inbound: { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'SMS Received' },
  sms_outbound: { icon: Send, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'SMS Sent' },
  customer_message: { icon: MessageCircle, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Customer' },
  
  // Calls
  call_inbound: { icon: Phone, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Inbound Call' },
  call_outbound: { icon: Phone, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Outbound Call' },

  // W4b: human-approved rollover decisions (server-emitted, 20270206061200)
  rollover_decision_raised: { icon: Calendar, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'End-of-day decision needed' },
  rollover_decision_recorded: { icon: CheckCircle2, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'End-of-day decision' },
  rollover_decision_expired: { icon: Calendar, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Needs placement' },
  rollover_plan_updated: { icon: Calendar, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Rollover plan' },
  // W4e: reserved-window schedule conflict (server-emitted, 20270206064000/064100)
  schedule_conflict_updated: { icon: Calendar, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Schedule conflict' },

  // Notes
  technician_note: { icon: Wrench, color: 'text-slate-600', bgColor: 'bg-slate-100', label: 'Tech Note' },
  advisor_note: { icon: User, color: 'text-violet-600', bgColor: 'bg-violet-100', label: 'Advisor Note' },
  manager_note: { icon: User, color: 'text-rose-600', bgColor: 'bg-rose-100', label: 'Manager Note' },
  
  // Job events
  job_added: { icon: Plus, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Job Added' },
  job_removed: { icon: Minus, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Job Removed' },
  job_note_added: { icon: FileText, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Job Note Added' },
  job_note_updated: { icon: FileText, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Job Note Updated' },
  job_time_edited: { icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Job Time Edited' },
  
  // Parts
  parts_ordered: { icon: Package, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Parts Ordered' },
  parts_delayed: { icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Parts Delayed' },
  parts_arrived: { icon: Package, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Parts Arrived' },
  
  // Reminders
  reminder_sent: { icon: Bell, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Reminder Sent' },
  reminder_confirmed: { icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Reminder Confirmed' },
  reminder_canceled: { icon: Bell, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Reminder Canceled' },
  
  // DVI & diagnostics
  dvi_finding_added: { icon: AlertTriangle, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'DVI Finding' },
  auto_bump: { icon: Clock, color: 'text-slate-600', bgColor: 'bg-slate-100', label: 'Auto Bump' },
  
  // AI
  ai_summary: { icon: Bot, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'AI Summary' },
  ai_risk_alert: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Risk Alert' },
  ai_recommendation: { icon: Bot, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'AI Recommendation' },
  
  // Voice
  voice_command: { icon: Mic, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Voice Command' },
  voice_transcript: { icon: Mic, color: 'text-slate-600', bgColor: 'bg-slate-100', label: 'Transcript' },

  // Workflow Report
  workflow_report_initialized: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Report Init' },
  workflow_complaint_added: { icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Complaint Added' },
  workflow_complaint_updated: { icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Complaint Updated' },
  workflow_note_added: { icon: FileText, color: 'text-slate-600', bgColor: 'bg-slate-100', label: 'Note Added' },
  workflow_note_updated: { icon: FileText, color: 'text-slate-600', bgColor: 'bg-slate-100', label: 'Note Updated' },
  workflow_inspection_started: { icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Inspection Started' },
  workflow_inspection_saved: { icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Inspection Saved' },
  workflow_inspection_completed: { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Inspection Completed' },
  workflow_diagnostic_created: { icon: Wrench, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Diag Created' },
  workflow_diagnostic_updated: { icon: Wrench, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Diag Updated' },
  workflow_diagnostic_started: { icon: Wrench, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Diag Started' },
  workflow_diagnostic_completed: { icon: Wrench, color: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Diag Completed' },

  // Operational events
  ticket_bay_changed: { icon: User, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Bay Changed' },
  ticket_status_changed: { icon: Play, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Status Changed' },
  ticket_reassigned: { icon: User, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Reassigned' },
  timer_stopped: { icon: Pause, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Timer Stopped' },

  // Diagnostic Load Guard
  dlg_advisory_shown: { icon: Shield, color: 'text-blue-500', bgColor: 'bg-blue-100', label: 'Diagnostic load assessed' },
  dlg_override_accepted: { icon: ShieldCheck, color: 'text-amber-500', bgColor: 'bg-amber-100', label: 'Assignment confirmed with advisory' },

  // Service Information
  service_info_acknowledgment: { icon: Compass, color: 'text-teal-600', bgColor: 'bg-teal-100', label: 'Service Info' },
  service_info_acknowledgment_updated: { icon: Wrench, color: 'text-teal-600', bgColor: 'bg-teal-100', label: 'Service Info Updated' },

  // FIX-R125-MED-1: Inspection timer + evidence + AI suggestion event types
  // were already valid in the OcisEventType taxonomy (src/utils/ocisIngest.ts)
  // and the server-side VALID_EVENT_TYPES whitelist (ocis-ingest/index.ts),
  // and they DO get emitted by useInspectionTimer + useInspectionEvidence —
  // but the UI EVENT_CONFIG map had no entries for them, so every inspection
  // event rendered as a generic gray "Event" row losing its semantic icon
  // and label. Adding configs here brings the UI back into line with the
  // truth-layer taxonomy. Same fix for artifact_* events that R125-HIGH-1
  // unblocked at the ingest layer.
  inspection_timer_started: { icon: Play, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Inspection Started' },
  inspection_timer_paused: { icon: Pause, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Inspection Paused' },
  inspection_timer_resumed: { icon: Play, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Inspection Resumed' },
  inspection_timer_stopped: { icon: Pause, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Inspection Stopped' },
  inspection_evidence_uploaded: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Evidence Uploaded' },
  inspection_evidence_placed: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Evidence Placed' },
  inspection_ai_suggestion: { icon: Bot, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'AI Suggestion' },

  // Artifacts (UI taxonomy parity with R125-HIGH-1 ingest fix)
  artifact_created: { icon: FileText, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Artifact Created' },
  artifact_linked: { icon: FileText, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Artifact Linked' },

  // OCIS AI Intake — Phase 1, Slice 1 (migration 20260524120000).
  // UI parity for the 6 intake event types added to the ocis_event_type
  // enum + VALID_EVENT_TYPES whitelist. No emitters yet; these configs
  // are wired so that when Slice 2 lands the events render with
  // semantic icons/labels rather than the generic DEFAULT_CONFIG row.
  intake_started: { icon: MessageSquare, color: 'text-cyan-600', bgColor: 'bg-cyan-100', label: 'Intake Started' },
  intake_audio_uploaded: { icon: Mic, color: 'text-cyan-600', bgColor: 'bg-cyan-100', label: 'Intake Audio' },
  intake_transcript_ready: { icon: FileText, color: 'text-cyan-600', bgColor: 'bg-cyan-100', label: 'Transcript Ready' },
  intake_draft_ready: { icon: Bot, color: 'text-cyan-600', bgColor: 'bg-cyan-100', label: 'AI Draft Ready' },
  intake_approved: { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Intake Approved' },
  intake_rejected: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Intake Rejected' },

  // OCIS AI Intake — Slice 3b (migration 20260525120000). Advisor-
  // selected suggested jobs from an intake draft were attached to the
  // ticket via intake-attach-jobs. Uses Wrench (icon already imported)
  // because the event is about jobs landing on the ticket.
  intake_jobs_attached: { icon: Wrench, color: 'text-cyan-600', bgColor: 'bg-cyan-100', label: 'Intake Jobs Attached' },

  // Custody handoffs (migration 20261214140000 — OCIS custody vocabulary
  // drift). Explicit rows rather than the generic DEFAULT_CONFIG, which would
  // render every one of these as a message icon labelled "Event" — actively
  // misleading for a transfer of RESPONSIBILITY between two people, and
  // indistinguishable from a note. Icons follow the kebab's own convention:
  // a person for the advisor side (FOH), a wrench for the technician side
  // (BOH). Exactly the seven canonical values; nothing else is added here.
  custody_kick_to_foh: { icon: User, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Sent to Advisor' },
  custody_foh_accept: { icon: CheckCircle2, color: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Advisor Accepted' },
  custody_kick_to_boh: { icon: Wrench, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Sent Back to Tech' },
  custody_boh_accept: { icon: CheckCircle2, color: 'text-teal-600', bgColor: 'bg-teal-100', label: 'Tech Accepted' },
  custody_reject_back: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Handoff Rejected' },
  custody_reassign_within_foh: { icon: User, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Advisor Reassigned' },
  custody_reassign_within_boh: { icon: Wrench, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Tech Reassigned' },
};

const DEFAULT_CONFIG = { 
  icon: MessageSquare, 
  color: 'text-muted-foreground', 
  bgColor: 'bg-muted', 
  label: 'Event' 
};

export function ThreadEventItem({ event, isLast = false }: ThreadEventItemProps) {
  const config = EVENT_CONFIG[event.event_type] || DEFAULT_CONFIG;
  const Icon = config.icon;
  // OCIS-R68-FIX (MED-3): render the event timestamp in the shop's
  // timezone, not the viewer's browser timezone. Events are stored as
  // timestamptz (UTC) and `format(new Date(...))` previously used the
  // viewer's local tz — so an advisor in PT and a technician in ET
  // looking at the same timeline would see different times next to the
  // same row. OCIS is the operational timeline for a *shop*, so the
  // shop's TZ is the authoritative rendering. Fall back to the raw
  // timestamp if the TimezoneProvider is not in scope (e.g. storybook).
  const { shopTimezone } = useShopTimezone();
  const formattedTime = shopTimezone
    ? formatInTimeZone(new Date(event.created_at), shopTimezone, 'h:mm a')
    : format(new Date(event.created_at), 'h:mm a');
  const formattedDate = shopTimezone
    ? formatInTimeZone(new Date(event.created_at), shopTimezone, 'MMM d')
    : format(new Date(event.created_at), 'MMM d');
  
  // Determine if this is a customer-facing message (SMS)
  const isCustomerMessage = event.event_type === 'sms_inbound' || event.event_type === 'customer_message';
  const isOutboundMessage = event.event_type === 'sms_outbound';
  const isCallEvent = event.event_type === 'call_inbound' || event.event_type === 'call_outbound';
  const payload = event.payload as Record<string, unknown> | undefined;
  
  return (
    <div className="relative flex gap-3 pb-4">
      {/* Timeline connector */}
      {!isLast && (
        <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
      )}
      
      {/* Icon */}
      <div className={cn(
        'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        config.bgColor
      )}>
        <Icon className={cn('h-4 w-4', config.color)} />
        {event.is_pinned && (
          <Pin className="absolute -top-1 -right-1 h-3 w-3 text-amber-500 fill-amber-500" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{config.label}</span>
          {event.author_name && (
            <>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground truncate">{event.author_name}</span>
            </>
          )}
          <span className="text-muted-foreground ml-auto text-xs shrink-0">
            {formattedDate} {formattedTime}
          </span>
        </div>
        
        {/* Message content */}
        {event.event_type === 'job_time_edited' && payload?.jobName ? (
          <div className="mt-1.5 rounded-lg p-3 text-sm bg-muted/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground">{String(payload.jobName)}</span>
              <span className="text-muted-foreground">
                {((payload.previousMinutes as number) / 60).toFixed(1)}h
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">
                {((payload.newMinutes as number) / 60).toFixed(1)}h
              </span>
            </div>
          </div>
        ) : event.event_type === 'ticket_reassigned' && payload?.fromTechName && payload?.toTechName ? (
          <div className="mt-1.5 rounded-lg p-3 text-sm bg-muted/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground">{String(payload.fromTechName)}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">{String(payload.toTechName)}</span>
            </div>
          </div>
        ) : event.event_type === 'ticket_moved_to_parking_lot' && payload?.fromTechName ? (
          <div className="mt-1.5 rounded-lg p-3 text-sm bg-muted/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground">{String(payload.fromTechName)}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-orange-600 dark:text-orange-400">Parking Lot</span>
              {payload.reason && (
                <span className="text-muted-foreground text-xs">({String(payload.reason)})</span>
              )}
            </div>
          </div>
        ) : event.event_type === 'ticket_moved_from_parking_lot' && payload?.toTechName ? (
          <div className="mt-1.5 rounded-lg p-3 text-sm bg-muted/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-orange-600 dark:text-orange-400">Parking Lot</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">{String(payload.toTechName)}</span>
            </div>
          </div>
        ) : event.content ? (
          <div className={cn(
            'mt-1.5 rounded-lg p-3 text-sm',
            isCustomerMessage && 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800',
            isOutboundMessage && 'bg-muted border border-border',
            !isCustomerMessage && !isOutboundMessage && 'bg-muted/50'
          )}>
            <p className="whitespace-pre-wrap break-words">{event.content}</p>
          </div>
        ) : null}

        {/* A custody handoff's free-form note ("Anything FOH should know"),
            from the event payload — the durable history of what the person
            said, without dumping the rest of the metadata. */}
        {event.event_type?.startsWith('custody_') && typeof payload?.notes === 'string' && payload.notes.trim() && (
          <p className="mt-1 text-sm italic text-foreground/80 whitespace-pre-wrap break-words" data-testid="thread-custody-notes">
            “{payload.notes.trim()}”
          </p>
        )}
        
        {/* Core-100 R4 (CORE-AF/AZ): how this reached the ticket (an inference is shown as one), and a
            customer's CANCEL reply shown as the request it is. */}
        {event.event_source === 'ai' && (
          <p className="mt-1 text-xs text-purple-700 dark:text-purple-300" data-testid="thread-derived">
            Derived by AI — not written by a person
          </p>
        )}
        {associationLabel(payload) && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="thread-association">
            {associationLabel(payload)}
          </p>
        )}
        {cancelRequestNote(payload) && (
          <p className="mt-1 text-xs font-medium text-red-700 dark:text-red-300" data-testid="thread-cancel-request">
            {cancelRequestNote(payload)}
          </p>
        )}

        {/* Call playback button — gated by VOIP exposure */}
        {isCallEvent && payload?.recording_available === true && payload?.call_event_id && (
          <ShowFeature feature="ui_voipCallIngestion">
            <div className="mt-1.5">
              <CallPlaybackButton
                callEventId={payload.call_event_id as string}
                shopId={event.shop_id}
                durationSeconds={payload.duration_seconds as number | undefined}
              />
            </div>
          </ShowFeature>
        )}

        {/* Core-100 R4 X2 (CORE-AQ): an appointment suggested from this call (derived), for the advisor to confirm on
            this ticket or reject. Renders nothing when there is no proposal or the viewer is not front office. */}
        {isCallEvent && typeof payload?.call_event_id === 'string' && (
          <CallAppointmentProposals callEventId={payload.call_event_id as string} ticketId={event.ticket_id ?? null} />
        )}
        {/* Core-100 R4 (CORE-AQ): a person corrects the call's ticket through relink_call_event (front office). */}
        {isCallEvent && typeof payload?.call_event_id === 'string' && (
          <FrontOfficeCallRelink callEventId={payload.call_event_id as string} currentTicketId={event.ticket_id ?? null} />
        )}

        {/* V-OCIS-Intake-Playback-Batch-v1: intake recording playback.
            Only renders when the event has an artifact_id payload key
            AND the event is bound to a ticket (ticket_id present).
            ThreadEvent guarantees shop_id but ticket_id may be null on
            inbox-only events; we require ticket_id because the signed-
            URL function gates on ticket ↔ artifact linkage. */}
        {event.event_type === 'intake_audio_uploaded'
          && event.ticket_id
          && typeof payload?.artifact_id === 'string'
          && (payload.artifact_id as string).length > 0 && (
          <IntakeRecordingPlayback
            shopId={event.shop_id}
            ticketId={event.ticket_id}
            artifactId={payload.artifact_id as string}
            hintProvider={typeof payload?.provider === 'string' ? (payload.provider as string) : null}
            hintDurationMs={typeof payload?.duration_ms === 'number' ? (payload.duration_ms as number) : null}
            hintSizeBytes={typeof payload?.size_bytes === 'number' ? (payload.size_bytes as number) : null}
            hintMimeType={typeof payload?.mime_type === 'string' ? (payload.mime_type as string) : null}
          />
        )}

        {/* Channel/source indicator */}
        {event.channel !== 'system' && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="capitalize">{event.channel}</span>
            {event.visibility === 'customer' && (
              <>
                <span>•</span>
                <span className="text-blue-600 dark:text-blue-400">Customer visible</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
